import authentication from "@irati/auth"
import {Business, User, DealsPackage, ProductPermissions} from "@irati/types"
import firstnameLastname from "@irati/identity/helpers/user/firstnameLastname"
import getDeviceLocale from "@irati/utils/lang/helpers/getDeviceLocale"
import domain from "../env/domain"
import env from "../env"
import API from "../services/API"
import IntercomMessenger, {boot, shutdown, update, show, showNewMessage, trackEvent, startTour} from "@intercom/messenger-js-sdk"
import {getIntercomKey, getIntercomBaseUrl} from "./config"
import BaseIntercom, {dealPackageToString, packageAddons, productAccessData} from "./base"
import {cancelIntercomJwtRefresh, getIntercomIdentityToken, scheduleIntercomJwtRefresh} from "./services/identityToken"
import type {IntercomIdentityToken} from "./services/identityToken"

class Intercom extends BaseIntercom {
  private identityToken: IntercomIdentityToken | null = null

  protected setup() {
    const shouldLoad = !env.IS_LOCAL && (domain.isTenant() || domain.isGlobal() || domain.isMerchant())
    if (shouldLoad) {
      IntercomMessenger({app_id: getIntercomKey(), region: "eu"})
    }
  }

  protected async setIdentity(user: User, business: Business, dealPackage?: DealsPackage, productPermissions?: ProductPermissions) {
    if (!window.Intercom) {
      return notLoaded()
    }

    // a ghosting admin must never be identified as the user, or they would see the user's conversations
    if (authentication.isUserGhost()) {
      return this.clearIdentity()
    }

    const identityToken = API.hasTokens() ? await getIntercomIdentityToken() : null
    // messenger security rejects a user without a token, so stay a visitor until updateIdentity retries
    if (!identityToken) {
      return this.clearIdentity()
    }

    const versionData = await this.getClientVersion()

    this.identityToken = identityToken
    if (this.identityToken.type === "jwt") {
      scheduleIntercomJwtRefresh({
        jwt: this.identityToken.jwt,
        onRefresh: (jwt) => {
          this.identityToken = {type: "jwt", jwt}
          if (window.Intercom) {
            update({intercom_user_jwt: jwt})
          }
        }
      })
    }

    shutdown()
    return boot({
      ...defaultIntercomSettings(),
      ...identityData(user, business, dealPackage, productPermissions),
      ...versionData,
      ...identityTokenSettings(this.identityToken)
    })
  }

  protected async updateIdentity(user: User, business: Business, dealPackage?: DealsPackage, productPermissions?: ProductPermissions) {
    if (window.Intercom && !authentication.isUserGhost()) {
      // the session may have resumed since setIdentity ran without a token
      if (!this.identityToken) {
        return this.setIdentity(user, business, dealPackage, productPermissions)
      }

      const versionData = await this.getClientVersion()

      update({
        ...identityData(user, business, dealPackage, productPermissions),
        ...versionData,
        ...identityTokenSettings(this.identityToken)
      })
    }
  }

  protected clearIdentity() {
    cancelIntercomJwtRefresh()
    this.identityToken = null
    if (window.Intercom) {
      shutdown()
      boot({
        ...defaultIntercomSettings()
      })
    }
  }

  showInAppMessages() {
    /* no-op on web */
  }

  hide() {
    if (window.Intercom) {
      update({
        hide_default_launcher: true
      })
    }
  }

  show(msg?: string) {
    if (window.Intercom) {
      // Ensure the default launcher is visible
      update({
        hide_default_launcher: false
      })
      // Explicitly open the chat window
      if (msg) {
        showNewMessage(msg)
      } else {
        show()
      }
    }
  }

  logEvent(eventName: string, data: any) {
    if (!window.Intercom) {
      return notLoaded()
    }
    return trackEvent(eventName, data)
  }

  addCustomMessage(message: string, url?: string) {
    if (window.Intercom) {
      showNewMessage(message)
      if (url) {
        update({Url: url})
      }
    }
  }

  startTour(tourId: number) {
    if (window.Intercom) {
      startTour(String(tourId))
    }
  }
}

export default new Intercom()

let thrown = false

function notLoaded() {
  if (!thrown) {
    // only throw once
    thrown = true
    // As 3rd party script, cannot always guarantee it will load
    console.error("Intercom failed to load")
  }

  return thrown
}

function identityTokenSettings(identityToken: IntercomIdentityToken | null) {
  if (identityToken?.type === "jwt") {
    return {intercom_user_jwt: identityToken.jwt}
  }
  if (identityToken?.type === "userHash") {
    return {user_hash: identityToken.userHash}
  }
  return {}
}

function defaultIntercomSettings() {
  return {
    api_base: getIntercomBaseUrl(),
    app_id: getIntercomKey()
  }
}

function identityData(user: User, business: Business, dealPackage?: DealsPackage, productPermissions?: ProductPermissions) {
  const role = user.role

  return {
    user_id: user.uuid,
    email: user.email,
    name: firstnameLastname(user),
    role,
    region: dealPackage?.region,
    device_locale: getDeviceLocale(),
    language_override: authentication.userLocale(),
    company: {
      company_id: business.uuid,
      name: business.businessName,
      tenant_group_id: business.groupIds,
      user_locale: user.language,
      tenant_uuid: business.uuid
    },
    monthlyTTValue: dealPackage?.monthlyTTValue,
    dealPackage: dealPackageToString(dealPackage),
    addons_enabled: packageAddons(dealPackage),
    product_access: productAccessData(productPermissions)
  }
}
